import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Popover } from '@mui/material';
import { InfoOutlined, ArrowDropDownRounded } from '@mui/icons-material';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import WbAutoRoundedIcon from '@mui/icons-material/WbAutoRounded';
import SpellcheckRoundedIcon from '@mui/icons-material/SpellcheckRounded';
import Tooltip from '@src/components/Tooltip';
import CommonButton from '@src/new-components/Button';
import actions from '@src/redux/actions';
import { ONBOARDING_POPUP_TYPE } from '@src/constants';
import { COLOR } from '@src/styles/color';
import { usePronunciationContext } from '@src/contexts/PronunciationCheckContext';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import {
  StyledAIEditorButton,
  StyledAIEditorPopover,
  StyledTooltipContent,
} from './index.style';

const AIEditorButton = () => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const {
    check,
    isLoading,
    isEditorEmpty,
    openSentences,
    estimatedCredits,
    isVbeeVietnameseVoice,
    voiceName,
  } = usePronunciationContext();
  const { getFeatureValue } = useFeatureFlags();
  const user = useSelector((state) => state.auth.user);

  const onboardingEnabled = getFeatureValue(
    FEATURE_KEYS.ONBOARDING_AI_PRONUNCIATION_CHECK,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenOnboardingPopup = (e) => {
    e?.stopPropagation();
    handleClose();
    dispatch(
      actions.onboardingPopup.showOnboardingPopup({
        type: ONBOARDING_POPUP_TYPE.AI_PRONUNCIATION_CHECK,
      }),
    );
  };

  const open = Boolean(anchorEl);
  const [isInfoHovered, setIsInfoHovered] = useState(false);
  const [isCreditsTooltipHovered, setIsCreditsTooltipHovered] = useState(false);

  const getCreditsTooltipTitle = () => {
    if (!isVbeeVietnameseVoice) {
      return (
        <span
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: t('aiEditorUnsupportedVoice', { voiceName }),
          }}
        />
      );
    }
    return t('costsCredit', {
      value: estimatedCredits.toLocaleString(language),
    });
  };

  const pronunciationTooltip = (
    <StyledTooltipContent>
      {t('aiEditorTooltipPronunciation')}{' '}
      {onboardingEnabled && (
        <span
          className="tooltip-link"
          role="button"
          tabIndex={0}
          onClick={handleOpenOnboardingPopup}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleOpenOnboardingPopup();
          }}
        >
          {t('aiEditorTooltipLink')}
        </span>
      )}
    </StyledTooltipContent>
  );

  return (
    <StyledAIEditorButton>
      <CommonButton
        variant="outlined"
        startIcon={
          <AutoAwesomeRoundedIcon
            sx={{ width: 24, height: 24, color: COLOR.blueV2 }}
          />
        }
        endIcon={
          <ArrowDropDownRounded
            sx={{
              width: 24,
              height: 24,
              color: COLOR.darkBlue,
              transform: anchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          />
        }
        onClick={handleClick}
        customStyle={{
          height: '44px',
          borderRadius: '480px',
          border: `1px solid ${COLOR.indigo[16]}`,
          padding: '8px 12px',
          textTransform: 'none',
          gap: '8px',
          minWidth: 'fit-content',
          '& .MuiButton-endIcon': {
            margin: '0px',
          },
          '&:hover': {
            borderColor: COLOR.indigo[32],
            background: COLOR.background,
          },
          '&:focus': {
            background: COLOR.white,
          },
        }}
      >
        <span className="ai-editor-btn-content">
          <span className="ai-editor-btn-text">{t('aiEditor')}</span>
          <span className="ai-editor-btn-beta">{t('betaV2')}</span>
        </span>
      </CommonButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.08)',
            marginTop: '8px',
          },
        }}
      >
        <StyledAIEditorPopover>
          {/* AI kiểm tra cách đọc */}
          <Tooltip
            arrow
            title={getCreditsTooltipTitle()}
            placement="top"
            open={isCreditsTooltipHovered && !isInfoHovered && !isEditorEmpty}
            onMouseEnter={() => setIsCreditsTooltipHovered(true)}
            onMouseLeave={() => setIsCreditsTooltipHovered(false)}
          >
            <span>
              <CommonButton
                variant="outlined"
                disabled={
                  isLoading ||
                  isEditorEmpty ||
                  openSentences ||
                  !isVbeeVietnameseVoice
                }
                startIcon={
                  <WbAutoRoundedIcon
                    sx={{ width: 24, height: 24, color: COLOR.darkBlue }}
                  />
                }
                onClick={() => {
                  handleClose();
                  check();
                }}
                customStyle={{
                  width: '100%',
                  justifyContent: 'flex-start',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: 'none!important',
                  background: COLOR.white,
                  textTransform: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    background: COLOR.indigo[8],
                  },
                  '&:focus': {
                    background: COLOR.white,
                  },
                }}
                textProps={{
                  variantText: 'body-14-medium',
                  color: COLOR.darkBlue,
                }}
                endIcon={
                  <Tooltip
                    arrow
                    title={pronunciationTooltip}
                    placement="top"
                    maxWidth="210px"
                    textAlign="center"
                    onMouseEnter={() => setIsInfoHovered(true)}
                    onClose={() => setIsInfoHovered(false)}
                  >
                    <InfoOutlined
                      sx={{ fontSize: 16, color: COLOR.loadingLoadRequest }}
                    />
                  </Tooltip>
                }
              >
                {t('aiEditorPronunciationCheck')}
              </CommonButton>
            </span>
          </Tooltip>
          {/* AI kiểm tra cách đọc */}
          <CommonButton
            variant="outlined"
            startIcon={
              <SpellcheckRoundedIcon
                sx={{ width: 24, height: 24, color: COLOR.darkBlue }}
              />
            }
            onClick={() => {}}
            customStyle={{
              width: '100%',
              justifyContent: 'flex-start',
              padding: '8px 12px',
              borderRadius: '8px',
              border: 'none!important',
              background: COLOR.white,
              textTransform: 'none',
              cursor: 'pointer',
              '&:hover': {
                background: COLOR.indigo[8],
              },
              '&:focus': {
                background: COLOR.white,
              },
            }}
            textProps={{
              variantText: 'body-14-medium',
              color: COLOR.darkBlue,
            }}
          >
            {t('aiSpellChecker')}{' '}
            <span className="coming-soon-text">({t('comingSoon')})</span>
          </CommonButton>
        </StyledAIEditorPopover>
      </Popover>
    </StyledAIEditorButton>
  );
};

export default AIEditorButton;
